// =============================================================================
// FILE: src/lib/constants.js
// MODULE: Core — App Constants
// PROJECT: Arcus Sports Management
//
// DESCRIPTION:
//   All magic strings and configuration constants used across the app.
//   Centralizing them here means changes happen in one place, not scattered
//   across dozens of files. Import what you need — tree-shaking removes the rest.
// =============================================================================

// ---------------------------------------------------------------------------
// USER ROLES
// Must match the user_role enum values in the database exactly.
// Used in RoleGuard, useAuth, and any role-based conditional rendering.
// ---------------------------------------------------------------------------
export const ROLES = {
  SUPER_ADMIN:  'super_admin',
  ADMIN:        'admin',
  TEAM_LEADER:  'teamleader',
  PLAYER:       'player',
}

// ---------------------------------------------------------------------------
// SUBSCRIPTION TIERS
// Must match the subscription_tier enum values in the database exactly.
// ---------------------------------------------------------------------------
export const TIERS = {
  DEMO:    'demo',
  STARTER: 'starter',
  PRO:     'pro',
  CLUB:    'club',
}

// ---------------------------------------------------------------------------
// SUBSCRIPTION STATUSES
// Must match the subscription_status enum values in the database exactly.
// ---------------------------------------------------------------------------
export const SUB_STATUS = {
  ACTIVE:   'active',
  TRIALING: 'trialing',
  PAST_DUE: 'past_due',
  CANCELED:  'canceled',
  UNPAID:   'unpaid',
}

// ---------------------------------------------------------------------------
// TOURNAMENT STATUSES
// Must match the tournament_status enum values in the database exactly.
// ---------------------------------------------------------------------------
export const TOURNAMENT_STATUS = {
  DRAFT:     'draft',
  ACTIVE:    'active',
  COMPLETED: 'completed',
  ARCHIVED:  'archived',
}

// ---------------------------------------------------------------------------
// MATCH STATUSES
// Must match the match_status enum values in the database exactly.
// ---------------------------------------------------------------------------
export const MATCH_STATUS = {
  SCHEDULED:   'scheduled',
  IN_PROGRESS: 'in_progress',
  COMPLETED:   'completed',
  CANCELED:    'canceled',
}

// ---------------------------------------------------------------------------
// REQUEST TYPES (approval workflow)
// Must match the request_type enum values in the database exactly.
// ---------------------------------------------------------------------------
export const REQUEST_TYPES = {
  PLAYER_JOIN_TEAM:            'player_join_team',
  TEAMLEADER_ADD_PLAYER:       'teamleader_add_player',
  TEAMLEADER_REMOVE_PLAYER:    'teamleader_remove_player',
  PLAYER_REGISTER_TOURNAMENT:  'player_register_tournament',
}

// ---------------------------------------------------------------------------
// SPORT KEYS
// Must match the sport_key values seeded in sport_configs table.
// ---------------------------------------------------------------------------
export const SPORTS = {
  SOCCER:        'soccer',
  VOLLEYBALL:    'volleyball',
  BASKETBALL:    'basketball',
  BASEBALL:      'baseball',
  RUGBY:         'rugby',
  FLAG_FOOTBALL: 'flag_football',
}

// ---------------------------------------------------------------------------
// ROUTE PATHS
// All app routes defined in one place.
// Import these in Link components and useNavigate calls — never hardcode paths.
// ---------------------------------------------------------------------------
export const ROUTES = {
  // Public
  HOME:         '/',
  LOGIN:        '/login',
  SIGNUP:       '/signup',
  FORGOT_PW:    '/forgot-password',
  RESET_PW:     '/reset-password',

  // Onboarding (post-signup, pre-dashboard)
  ONBOARDING:   '/onboarding',

  // Admin
  ADMIN_DASHBOARD:      '/admin',
  ADMIN_TOURNAMENTS:    '/admin/tournaments',
  ADMIN_TOURNAMENT:     '/admin/tournaments/:id',
  ADMIN_TEAMS:          '/admin/tournaments/:id/teams',
  ADMIN_MATCHES:        '/admin/tournaments/:id/matches',
  ADMIN_PLAYERS:        '/admin/players',
  ADMIN_SETTINGS:       '/admin/settings',
  ADMIN_BILLING:        '/admin/billing',

  // Matches (referee scoring view)
  MATCH_SCORING:        '/match/:id/score',
  VOLLEYBALL_SCORING: '/volleyball/:id/score',
  RUGBY_SCORING:      '/rugby/:id/score',
  ADMIN_ATHLETICS:    '/admin/athletics',
  ADMIN_ATHLETE:      '/admin/athletics/:id',
  ADMIN_ATH_EVENTS:   '/admin/athletics/events',
  ADMIN_ATH_EVENT:    '/admin/athletics/events/:id',
  ADMIN_ATH_JOIN:     '/admin/athletics/join/:code',

  // Player
  PLAYER_DASHBOARD:     '/player',
  PLAYER_PROFILE:       '/player/profile',
  PLAYER_TOURNAMENT:    '/player/tournaments/:id',

  // Super admin
  SUPER_ADMIN:          '/superadmin',
}

// ---------------------------------------------------------------------------
// QUERY KEYS
// TanStack Query cache keys — used in useQuery and useMutation calls.
// Centralizing prevents cache key typos causing stale data bugs.
// Format: ['entity', 'scope', ...identifiers]
// ---------------------------------------------------------------------------
export const QUERY_KEYS = {
  // Auth & session
  SESSION:          ['session'],
  MY_ROLE:          ['my-role'],
  MY_SUBSCRIPTION:  ['my-subscription'],

  // Tournaments
  TOURNAMENTS:       (tenantId)        => ['tournaments', tenantId],
  TOURNAMENT:        (id)              => ['tournament', id],

  // Teams
  TEAMS:             (tournamentId)    => ['teams', tournamentId],
  TEAM:              (id)              => ['team', id],

  // Matches
  MATCHES:           (tournamentId)    => ['matches', tournamentId],
  MATCH:             (id)              => ['match', id],
  MATCH_STATS:       (matchId)         => ['match-stats', matchId],

  // Players & users
  USERS:             (tenantId)        => ['users', tenantId],
  USER:              (id)              => ['user', id],

  // Standings
  STANDINGS:         (tournamentId)    => ['standings', tournamentId],

  // Pending requests
  REQUESTS:          (tenantId, type)  => ['requests', tenantId, type],

  // Sports
  SPORT_CONFIGS:     ()                => ['sport-configs'],
}

// ---------------------------------------------------------------------------
// PAGINATION
// Default page sizes for list views.
// ---------------------------------------------------------------------------
export const PAGE_SIZE = {
  DEFAULT:     20,
  PLAYERS:     50,
  MATCHES:     20,
  AUDIT_LOG:   50,
}

// ---------------------------------------------------------------------------
// LOCAL STORAGE KEYS
// Prevent key collisions across sessions and future features.
// ---------------------------------------------------------------------------
export const STORAGE_KEYS = {
  THEME:         'arcus:theme',         // 'light' | 'dark' | 'system'
  LAST_TENANT:   'arcus:last-tenant',   // UUID — restore last active tenant on login
}
